import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Tooltip,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ChatIcon from "@mui/icons-material/Chat";
import ShareIcon from "@mui/icons-material/Share";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import PeopleIcon from "@mui/icons-material/People";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import { SourcePlatform } from "../SourcePlatform";
import {
  getMetricLabel,
  getMetricTooltip,
  getMultiAccountShareTooltip,
} from "../../constants";
import { PrivateContent } from "../../services/api";
import {
  getContentMetricsUpdatedAt,
  getInsightsCollectedAt,
  getLatestContentMetrics,
  getYoutubeAudienceFiveColumns,
  formatYoutubeAudiencePercent,
} from "../../utils/contentMetricsUtils";
import { MetricHoverTitle } from "../metrics/MetricHoverTitle";
import { apiService } from "../../services/api";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import axios from "axios";

interface ContentStatisticsProps {
  content: PrivateContent;
  linkedContent?: PrivateContent | null;
  showTitle?: boolean;
  title?: string;
  mentionTime?: number; // Time in seconds for calculating estimated viewers
  onContentUpdated?: (updatedContent: PrivateContent) => void;
  readOnly?: boolean; // Disable editing for mentions view
  includeEstimatedViewers?: boolean; // Whether to show estimated viewers metric
  includeInstagramReach?: boolean; // Whether to show Instagram reach metric
  includeLiveViewers?: boolean; // Whether to show livestream viewers metric
  /** @deprecated Prefer liveAverageViewers / liveViewersAtMention */
  liveViewers?: number | null;
  /** Episode-level average concurrent viewers (Generales → En vivo) */
  liveAverageViewers?: number | null;
  /** Episode-level peak concurrent viewers (Generales → En vivo) */
  livePeakViewers?: number | null;
  /** Concurrent viewers at mention time (En el momento de la mención) */
  liveViewersAtMention?: number | null;
}

// Spotify-specific helper functions
const getStreams = (item: PrivateContent) => {
  return item?.insights?.streams || 0;
};

const getUniqueListeners = (item: PrivateContent) => {
  return item?.insights?.uniqueListeners || 0;
};

const getAverageListenPct = (item: PrivateContent) => {
  return item?.insights?.averageListenPct || 0;
};

const getImpressions = (item: PrivateContent) => {
  return item?.insights?.impressions || 0;
};

const getFollowersGained = (item: PrivateContent) => {
  return item?.insights?.followersGained || 0;
};

/** Show Métricas when insights exist, daily snapshots exist, or root-level counters are present. */
const hasDisplayableMetrics = (item: PrivateContent): boolean => {
  if (Object.keys(item.insights || {}).length > 0) return true;
  if (Array.isArray(item.dailyMetrics) && item.dailyMetrics.length > 0) {
    return true;
  }
  {
    const d = getLatestContentMetrics(item, item.source);
    if (
      d.views > 0 ||
      d.likes > 0 ||
      d.comments > 0 ||
      d.shares > 0 ||
      d.saves > 0 ||
      d.reach > 0
    ) {
      return true;
    }
  }
  if (
    getStreams(item) > 0 ||
    getUniqueListeners(item) > 0 ||
    getAverageListenPct(item) > 0
  ) {
    return true;
  }
  if (getImpressions(item) > 0 || getFollowersGained(item) > 0) return true;
  return false;
};

const hasYoutubeOAuthDemographics = (item: PrivateContent): boolean => {
  const d = item?.insights?.demographics;
  if (!d) return false;
  return (d.gender?.length ?? 0) > 0 || (d.ageRanges?.length ?? 0) > 0;
};

/** Safe string for API paths — avoids accidental `undefined` in URLs (backend returns 404). */
const normalizePrivateContentId = (content: PrivateContent): string | null => {
  const raw = content?._id as unknown;
  if (raw == null) return null;
  const s = typeof raw === "string" ? raw.trim() : String(raw).trim();
  if (!s || s === "undefined" || s === "null") return null;
  return s;
};

/** Five audience columns aligned with PublicSponsorLink (getYoutubeAudienceFiveColumns + formatYoutubeAudiencePercent). */
const buildYoutubeAudienceMetricRows = (content: PrivateContent) => {
  if (content.source !== "youtube" || !hasYoutubeOAuthDemographics(content)) {
    return [];
  }
  /** Company Config «Demográficos» per program (`programDemographicDataEnabled` from GET content). */
  if (content.programDemographicDataEnabled !== true) {
    return [];
  }
  const m = getLatestContentMetrics(content, "youtube");
  const audienceFive = getYoutubeAudienceFiveColumns(m.demographics);
  const icon = <GroupsIcon sx={{ fontSize: 16, color: "primary.main" }} />;
  return [
    {
      condition: true,
      icon,
      label: "Hombres",
      value: 0,
      tooltip: "Distribución por género (YouTube Analytics OAuth)",
      formattedValue: formatYoutubeAudiencePercent(audienceFive.hombres),
    },
    {
      condition: true,
      icon,
      label: "Mujeres",
      value: 0,
      tooltip: "Distribución por género (YouTube Analytics OAuth)",
      formattedValue: formatYoutubeAudiencePercent(audienceFive.mujeres),
    },
    {
      condition: true,
      icon,
      label: "18–24",
      value: 0,
      tooltip: "Edad 18–24",
      formattedValue: formatYoutubeAudiencePercent(audienceFive.edad18a24),
    },
    {
      condition: true,
      icon,
      label: "25–34",
      value: 0,
      tooltip: "Edad 25–34",
      formattedValue: formatYoutubeAudiencePercent(audienceFive.edad25a34),
    },
    {
      condition: true,
      icon,
      label: "35+",
      value: 0,
      tooltip: "Edad 35 años o más",
      formattedValue: formatYoutubeAudiencePercent(audienceFive.edad35plus),
    },
  ];
};

/** Manual OAuth metrics (espectadores únicos, impresiones) — concatenated into the main Audiencia metric row. */
const buildYoutubeManualAudienceMetricRows = (content: PrivateContent) => {
  if (content.source !== "youtube") {
    return [];
  }
  const ins = content.insights;
  const rows: Array<{
    icon: React.ReactNode;
    label: string;
    value: number;
    tooltip: string;
    formattedValue: string;
  }> = [];

  const uv = ins?.uniqueViewers;
  if (uv != null && !Number.isNaN(Number(uv))) {
    const n = Math.floor(Number(uv));
    rows.push({
      icon: <PersonIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("espectadoresUnicos", "long"),
      value: n,
      tooltip: getMetricTooltip("espectadoresUnicos", "youtube"),
      formattedValue: n.toLocaleString("es-ES"),
    });
  }

  const ti = ins?.thumbnailImpressions;
  if (ti != null && !Number.isNaN(Number(ti))) {
    const n = Math.floor(Number(ti));
    rows.push({
      icon: <ImageOutlinedIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: "Impresiones",
      value: n,
      tooltip: getMetricTooltip("impresionesMiniatura", "youtube"),
      formattedValue: n.toLocaleString("es-ES"),
    });
  }

  return rows;
};

// Helper function to calculate engagement rate
// YouTube: (likes + comments + shares) / views * 100
// Instagram: (likes + comments + saves) / reach * 100 (or views if reach not available)
const getEngagementRate = (item: PrivateContent): number => {
  const m = getLatestContentMetrics(item, item.source);
  const likes = m.likes;
  const comments = m.comments;
  const source = item.source as string;

  if (item.source === "youtube") {
    const views = m.views;
    if (views === 0) return 0;
    const shares = m.shares;
    const totalEngagement = likes + comments + shares;
    return (totalEngagement / views) * 100;
  } else if (source === "instagram" || source === "instagram_private") {
    const reach = m.reach;
    const views = m.views;
    const denominator = reach > 0 ? reach : views;
    if (denominator === 0) return 0;
    const saves = m.saves;
    const totalEngagement = likes + comments + saves;
    return (totalEngagement / denominator) * 100;
  }

  return 0;
};

// Helper function to get estimated viewers at a specific time using retention data
export const getEstimatedViewersAtTime = (
  content: PrivateContent,
  timeInSeconds: number,
): number | null => {
  if (
    content.source !== "youtube" ||
    !content.insights?.audienceRetention ||
    !content.duration
  ) {
    return null;
  }

  const retentionData = content.insights.audienceRetention;
  const videoDurationSeconds = content.duration;
  const totalViews = getLatestContentMetrics(content, "youtube").views;

  if (retentionData.length === 0 || totalViews === 0) {
    return null;
  }

  // Calculate time ratio (0.0 to 1.0)
  const timeRatio = timeInSeconds / videoDurationSeconds;

  // Special case: if asking for exactly 0 seconds or very close to start, assume 100% retention
  if (timeInSeconds <= 1) {
    return totalViews;
  }

  // For very beginning (1-5 seconds), use actual retention data if available
  if (timeInSeconds <= 5) {
    // Find the earliest data point
    const earliestPoint = retentionData.reduce((earliest: any, current: any) =>
      current.elapsedVideoTimeRatio < earliest.elapsedVideoTimeRatio
        ? current
        : earliest,
    );

    // Use the earliest data point we have
    return Math.round(totalViews * earliestPoint.audienceWatchRatio);
  }

  // Find the closest retention data point
  const closest = retentionData.reduce((prev: any, curr: any) =>
    Math.abs(curr.elapsedVideoTimeRatio - timeRatio) <
    Math.abs(prev.elapsedVideoTimeRatio - timeRatio)
      ? curr
      : prev,
  );

  // Calculate estimated viewers
  const estimatedViewers = Math.round(totalViews * closest.audienceWatchRatio);
  return estimatedViewers;
};

// Helper function to format large numbers (e.g., 1234 -> "1.2K")
const formatViewerCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  } else if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
};

// Component for rendering a single metric
const MetricItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  isPercentage?: boolean;
  tooltip?: string;
  formattedValue?: string;
  vertical?: boolean;
  /** When this metric's numbers were collected. */
  metricsUpdatedAt?: string | null;
}> = ({
  icon,
  label,
  value,
  isPercentage = false,
  tooltip,
  formattedValue,
  vertical = false,
  metricsUpdatedAt,
}) => (
  <Tooltip
    title={
      <MetricHoverTitle
        label={tooltip || label}
        updatedAt={metricsUpdatedAt}
      />
    }
    arrow
    placement="top"
    componentsProps={{
      tooltip: {
        sx: {
          textAlign: "center",
          maxWidth: 280,
        },
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: vertical ? "row" : "column",
        alignItems: "center",
        justifyContent: vertical ? "space-between" : "center",
        gap: vertical ? 1 : 0,
        minWidth: vertical ? undefined : 90,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        bgcolor: "action.hover",
        cursor: "default",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 500,
          color: "text.secondary",
          textAlign: vertical ? "left" : "center",
          lineHeight: 1.2,
          mb: vertical ? 0 : 0.5,
          flex: vertical ? 1 : undefined,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {icon}
        <Typography variant="body2" fontWeight={600}>
          {formattedValue ||
            (isPercentage
              ? `${value.toFixed(1)}%`
              : value.toLocaleString("es-ES"))}
        </Typography>
      </Stack>
    </Box>
  </Tooltip>
);

/** YouTube OAuth demographics — section below Métricas; optional manual unique viewers + thumbnail impressions. */
const YoutubeAudienceSection: React.FC<{
  content: PrivateContent;
  onContentUpdated?: (c: PrivateContent) => void;
  readOnly?: boolean;
  /** Primary item in the dialog — used to invalidate `contentLink` after saving the linked YouTube row. */
  primaryDialogContentId?: string;
}> = ({
  content,
  onContentUpdated,
  readOnly = false,
  primaryDialogContentId,
}) => {
  const queryClient = useQueryClient();
  const contentIdForProgramFlag = normalizePrivateContentId(content);
  const shouldFetchProgramDemoFlag =
    content.source === "youtube" &&
    content.programDemographicDataEnabled === undefined &&
    contentIdForProgramFlag != null;

  const { data: enrichedYoutubeContent } = useQuery({
    queryKey: ["private-content-by-id", contentIdForProgramFlag],
    queryFn: () =>
      apiService.getPrivateContentById(contentIdForProgramFlag as string),
    enabled: shouldFetchProgramDemoFlag,
    staleTime: 60 * 1000,
  });

  const contentForAudienceMetrics =
    shouldFetchProgramDemoFlag && enrichedYoutubeContent != null
      ? enrichedYoutubeContent
      : content;

  const audienceMetrics =
    buildYoutubeAudienceMetricRows(contentForAudienceMetrics);
  const manualAudienceMetrics = buildYoutubeManualAudienceMetricRows(content);
  const [formOpen, setFormOpen] = useState(false);
  const [uniqueViewersInput, setUniqueViewersInput] = useState("");
  const [thumbnailImpressionsInput, setThumbnailImpressionsInput] =
    useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ins = content.insights;
    setUniqueViewersInput(
      ins?.uniqueViewers != null && !Number.isNaN(Number(ins.uniqueViewers))
        ? String(ins.uniqueViewers)
        : "",
    );
    setThumbnailImpressionsInput(
      ins?.thumbnailImpressions != null &&
        !Number.isNaN(Number(ins.thumbnailImpressions))
        ? String(ins.thumbnailImpressions)
        : "",
    );
    setError(null);
  }, [content]);

  if (audienceMetrics.length === 0 && manualAudienceMetrics.length === 0) {
    return null;
  }

  /** Demographics + espectadores únicos / impresiones in one scroll row */
  const allAudienceRowItems = [...audienceMetrics, ...manualAudienceMetrics];

  const handleSave = async () => {
    setError(null);
    const payload: {
      uniqueViewers?: number;
      thumbnailImpressions?: number;
    } = {};
    const uvTrim = uniqueViewersInput.trim();
    const tiTrim = thumbnailImpressionsInput.trim();
    if (uvTrim !== "") {
      const n = parseInt(uvTrim, 10);
      if (Number.isNaN(n) || n < 0) {
        setError("Espectadores únicos: número válido ≥ 0");
        return;
      }
      payload.uniqueViewers = n;
    }
    if (tiTrim !== "") {
      const n = parseInt(tiTrim, 10);
      if (Number.isNaN(n) || n < 0) {
        setError("Impresiones: número válido ≥ 0");
        return;
      }
      payload.thumbnailImpressions = n;
    }
    if (Object.keys(payload).length === 0) {
      setError("Indica al menos un valor.");
      return;
    }
    const contentId = normalizePrivateContentId(content);
    if (!contentId) {
      setError(
        "ID de contenido no válido. Cierra el diálogo y vuelve a abrirlo.",
      );
      return;
    }
    setSaving(true);
    try {
      const res = await apiService.updateYoutubeOAuthManualMetrics(
        contentId,
        payload,
      );
      await queryClient.invalidateQueries({
        queryKey: ["private-content", contentId],
      });
      if (primaryDialogContentId) {
        await queryClient.invalidateQueries({
          queryKey: ["contentLink", primaryDialogContentId],
        });
      }
      onContentUpdated?.({
        ...content,
        insights: {
          ...content.insights,
          ...res.data.insights,
        },
      });
      setFormOpen(false);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const msg = (e.response?.data as { message?: string })?.message;
        setError(
          msg ||
            (e.response?.status === 404
              ? "Contenido no encontrado. Comprueba que el vídeo sigue en la colección."
              : e.message) ||
            "Error al guardar",
        );
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Error al guardar");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h3" color="text" sx={{ mb: 2 }}>
        Audiencia
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ pt: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
              pb: 0.5,
            }}
          >
            {allAudienceRowItems.map((metric, index) => (
              <Box key={`audience-row-${index}`} sx={{ minWidth: 0 }}>
                <MetricItem
                  icon={metric.icon}
                  label={metric.label}
                  value={metric.value}
                  tooltip={metric.tooltip}
                  formattedValue={metric.formattedValue}
                  vertical={false}
                  metricsUpdatedAt={getInsightsCollectedAt(content)}
                />
              </Box>
            ))}
          </Box>

          {audienceMetrics.length > 0 && !readOnly && (
            <>
              <Box
                sx={{
                  mt: 2,
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="text"
                  onClick={() => {
                    setFormOpen((o) => !o);
                    setError(null);
                  }}
                  sx={{
                    p: 0,
                    minWidth: 0,
                    textTransform: "none",
                    textDecoration: "underline",
                    justifyContent: "flex-end",
                    fontSize: "0.875rem",
                  }}
                >
                  Agregar espectadores únicos e impresiones
                </Button>
              </Box>

              {formOpen && (
                <Box
                  sx={{
                    mt: 2.5,
                    pt: 2.5,
                    width: "100%",
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Métricas manuales (YouTube)
                  </Typography>
                  <Stack spacing={2.5} sx={{ width: "100%" }}>
                    {error && (
                      <Alert severity="error" sx={{ width: "100%" }}>
                        {error}
                      </Alert>
                    )}
                    <TextField
                      label="Espectadores únicos"
                      type="number"
                      fullWidth
                      value={uniqueViewersInput}
                      onChange={(e) => setUniqueViewersInput(e.target.value)}
                      inputProps={{ min: 0, step: 1 }}
                      disabled={saving}
                      placeholder="Ej. 12500"
                    />
                    <TextField
                      label="Impresiones"
                      type="number"
                      fullWidth
                      value={thumbnailImpressionsInput}
                      onChange={(e) =>
                        setThumbnailImpressionsInput(e.target.value)
                      }
                      inputProps={{ min: 0, step: 1 }}
                      disabled={saving}
                      placeholder="Ej. 480000"
                    />
                    <Stack
                      direction={{ xs: "column-reverse", sm: "row" }}
                      spacing={1.5}
                      justifyContent="flex-end"
                      sx={{ width: "100%", pt: 0.5 }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setFormOpen(false);
                          setError(null);
                        }}
                        disabled={saving}
                        sx={{ minWidth: { sm: 120 } }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        startIcon={
                          saving ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : undefined
                        }
                        sx={{ minWidth: { sm: 140 } }}
                      >
                        {saving ? "Guardando…" : "Guardar"}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

/** Keep only digits (integer metrics). Strips spaces, dots, letters that clear type=number. */
const sanitizeIntegerInput = (raw: string) => raw.replace(/\D/g, "");

/** Keep digits + a single decimal point (retention %). */
const sanitizeDecimalInput = (raw: string) => {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const [head, ...rest] = cleaned.split(".");
  return rest.length > 0 ? `${head}.${rest.join("").replace(/\./g, "")}` : head;
};

// Component for rendering Spotify-specific metrics
const SpotifyMetricsCard: React.FC<{
  contentId: string;
  onMetricsUpdated?: (metrics: {
    streams: number;
    uniqueListeners: number;
  }) => void;
  readOnly?: boolean;
}> = ({ contentId, onMetricsUpdated, readOnly = false }) => {
  const queryClient = useQueryClient();
  const stableContentId = String(contentId);
  const [isEditing, setIsEditing] = useState(false);
  const [metrics, setMetrics] = useState({
    streams: "",
    uniqueListeners: "",
    averageListenPct: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Use React Query to fetch content data
  const { data: content, isLoading: contentLoading } = useQuery({
    queryKey: ["private-content", stableContentId],
    queryFn: () => apiService.getPrivateContentById(stableContentId),
    refetchOnMount: "always",
  });

  const handleInputChange =
    (field: "streams" | "uniqueListeners" | "averageListenPct") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const value =
        field === "averageListenPct"
          ? sanitizeDecimalInput(raw)
          : sanitizeIntegerInput(raw);
      setMetrics((prev) => ({ ...prev, [field]: value }));

      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
      setSubmitError(null);
    };

  const hasSpotifyMetrics = content
    ? (getStreams(content) > 0 ||
        getUniqueListeners(content) > 0 ||
        getImpressions(content) > 0 ||
        getFollowersGained(content) > 0) &&
      !(getStreams(content) === 0 && getUniqueListeners(content) === 0)
    : false;

  // Keep the edit form mounted while typing — only block on the initial fetch.
  if ((contentLoading && !content) || !content) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Cargando métricas...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const validateMetrics = () => {
    const newErrors: { [key: string]: string } = {};

    // Check if values are provided
    if (!metrics.streams.trim()) {
      newErrors.streams = "Requerido";
    } else if (isNaN(Number(metrics.streams)) || Number(metrics.streams) < 0) {
      newErrors.streams = "Número válido ≥ 0";
    }

    if (!metrics.uniqueListeners.trim()) {
      newErrors.uniqueListeners = "Requerido";
    } else if (
      isNaN(Number(metrics.uniqueListeners)) ||
      Number(metrics.uniqueListeners) < 0
    ) {
      newErrors.uniqueListeners = "Número válido ≥ 0";
    }

    // Optional: validate retention only if provided
    if (metrics.averageListenPct.trim()) {
      const retentionValue = Number(metrics.averageListenPct);
      if (isNaN(retentionValue) || retentionValue < 0 || retentionValue > 100) {
        newErrors.averageListenPct = "0-100";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasAllZeros = () => {
    const streams = Number(metrics.streams);
    const uniqueListeners = Number(metrics.uniqueListeners);
    return streams === 0 && uniqueListeners === 0;
  };

  const handleSubmit = async () => {
    if (!validateMetrics()) {
      return;
    }

    setIsLoading(true);
    setSubmitError(null);

    try {
      const metricsData: {
        streams: number;
        uniqueListeners: number;
        averageListenPct?: number;
      } = {
        streams: Number(metrics.streams),
        uniqueListeners: Number(metrics.uniqueListeners),
      };

      // Only include retention if provided
      if (metrics.averageListenPct.trim()) {
        metricsData.averageListenPct = Number(metrics.averageListenPct);
      }

      await apiService.updateContentSpotifyMetrics(stableContentId, metricsData);

      // Invalidate this specific content query to trigger a refetch
      await queryClient.invalidateQueries({
        queryKey: ["private-content", stableContentId],
      });

      if (onMetricsUpdated) {
        onMetricsUpdated(metricsData);
      }

      setIsEditing(false);
      setMetrics({ streams: "", uniqueListeners: "", averageListenPct: "" });
    } catch (error: any) {
      console.error("Error updating Spotify metrics:", error);
      setSubmitError(
        error.response?.data?.message || "Error al actualizar las métricas",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setMetrics({ streams: "", uniqueListeners: "", averageListenPct: "" });
    setErrors({});
    setSubmitError(null);
  };

  const handleEditClick = () => {
    // Pre-fill form with existing metrics when editing
    const existingRetention = getAverageListenPct(content);
    setMetrics({
      streams: getStreams(content).toString(),
      uniqueListeners: getUniqueListeners(content).toString(),
      averageListenPct:
        existingRetention > 0 ? existingRetention.toString() : "",
    });
    setIsEditing(true);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setSubmitError(null);

    try {
      // Set all metrics to 0 to effectively delete them
      const metricsData = {
        streams: 0,
        uniqueListeners: 0,
        averageListenPct: 0,
      };

      await apiService.updateContentSpotifyMetrics(stableContentId, metricsData);

      // Invalidate this specific content query to trigger a refetch
      await queryClient.invalidateQueries({
        queryKey: ["private-content", stableContentId],
      });

      if (onMetricsUpdated) {
        onMetricsUpdated(metricsData);
      }

      setIsEditing(false);
      setMetrics({ streams: "", uniqueListeners: "", averageListenPct: "" });
    } catch (error: any) {
      console.error("Error deleting Spotify metrics:", error);
      setSubmitError(
        error.response?.data?.message || "Error al eliminar las métricas",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasSpotifyMetrics) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={2}>
            {!isEditing || readOnly ? (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  Sin métricas de Spotify disponibles
                </Typography>
                {!readOnly && (
                  <Button
                    variant="outlined"
                    onClick={() => setIsEditing(true)}
                    size="small"
                    fullWidth
                  >
                    Agregar métricas
                  </Button>
                )}
              </>
            ) : (
              <Stack spacing={2}>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  textAlign="center"
                >
                  Agregar métricas de Spotify
                </Typography>

                {submitError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {submitError}
                  </Alert>
                )}

                <Stack spacing={1.5}>
                  <TextField
                    label="Reproducciones"
                    type="text"
                    value={metrics.streams}
                    onChange={handleInputChange("streams")}
                    error={!!errors.streams}
                    helperText={errors.streams}
                    size="small"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    disabled={isLoading}
                  />

                  <TextField
                    label="Espectadores"
                    type="text"
                    value={metrics.uniqueListeners}
                    onChange={handleInputChange("uniqueListeners")}
                    error={!!errors.uniqueListeners}
                    helperText={errors.uniqueListeners}
                    size="small"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    disabled={isLoading}
                  />

                  <TextField
                    label={`${getMetricLabel("retencionPromedio", "long")} (optional)`}
                    type="text"
                    value={metrics.averageListenPct}
                    onChange={handleInputChange("averageListenPct")}
                    error={!!errors.averageListenPct}
                    helperText={
                      errors.averageListenPct ||
                      "Porcentaje promedio de escucha (0-100)"
                    }
                    size="small"
                    inputProps={{ inputMode: "decimal" }}
                    disabled={isLoading}
                  />
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={handleCancel}
                    disabled={isLoading}
                    size="small"
                    variant="outlined"
                    sx={{ flex: 1 }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    size="small"
                    variant="contained"
                    startIcon={
                      isLoading ? <CircularProgress size={16} /> : <SaveIcon />
                    }
                    sx={{ flex: 1 }}
                  >
                    {isLoading ? "Guardando..." : "Guardar"}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const spotifyMetrics = [
    {
      condition: getStreams(content) > 0,
      icon: <HeadphonesIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("reproducciones", "long"),
      value: getStreams(content),
      tooltip: getMetricTooltip("reproducciones"),
      formattedValue: undefined,
    },
    {
      condition: getUniqueListeners(content) > 0,
      icon: <PersonIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("espectadores", "long"),
      value: getUniqueListeners(content),
      tooltip: getMetricTooltip("espectadores"),
      formattedValue: undefined,
    },
    {
      condition: getAverageListenPct(content) > 0,
      icon: <TrendingUpIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("retencionPromedio", "long"),
      value: getAverageListenPct(content),
      isPercentage: true,
      tooltip: getMetricTooltip("retencionPromedio"),
      formattedValue: undefined,
    },
  ].filter((metric) => metric.condition);

  if (isEditing) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="body2" fontWeight="medium" textAlign="center">
              Editar métricas de Spotify
            </Typography>

            {submitError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {submitError}
              </Alert>
            )}

            <Stack spacing={1.5}>
              <TextField
                label={getMetricLabel("reproducciones", "long")}
                type="text"
                value={metrics.streams}
                onChange={handleInputChange("streams")}
                error={!!errors.streams}
                helperText={errors.streams}
                size="small"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                disabled={isLoading}
              />

              <TextField
                label={getMetricLabel("espectadores", "long")}
                type="text"
                value={metrics.uniqueListeners}
                onChange={handleInputChange("uniqueListeners")}
                error={!!errors.uniqueListeners}
                helperText={errors.uniqueListeners}
                size="small"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                disabled={isLoading}
              />

              <TextField
                label={`${getMetricLabel("retencionPromedio", "long")} (optional)`}
                type="text"
                value={metrics.averageListenPct}
                onChange={handleInputChange("averageListenPct")}
                error={!!errors.averageListenPct}
                helperText={
                  errors.averageListenPct ||
                  "Porcentaje promedio de escucha (0-100)"
                }
                size="small"
                inputProps={{ inputMode: "decimal" }}
                disabled={isLoading}
              />
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                onClick={handleCancel}
                disabled={isLoading}
                size="small"
                variant="outlined"
                sx={{ flex: 1 }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isLoading}
                size="small"
                variant="outlined"
                color="error"
                startIcon={
                  isLoading ? <CircularProgress size={16} /> : <DeleteIcon />
                }
                sx={{ flex: 1 }}
              >
                {isLoading ? "Eliminando..." : "Borrar"}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isLoading}
                size="small"
                variant="contained"
                startIcon={
                  isLoading ? <CircularProgress size={16} /> : <SaveIcon />
                }
                sx={{ flex: 1 }}
              >
                {isLoading
                  ? "Guardando..."
                  : hasAllZeros()
                    ? "Limpiar métricas"
                    : "Guardar"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Métricas de Spotify
            </Typography>
            {!readOnly && (
              <Typography
                onClick={handleEditClick}
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: "primary.main",
                  fontSize: "0.875rem",
                  "&:hover": {
                    color: "primary.dark",
                  },
                }}
              >
                Editar
              </Typography>
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            {spotifyMetrics.map((metric, index) => (
              <MetricItem
                key={index}
                icon={metric.icon}
                label={metric.label}
                value={metric.value}
                isPercentage={metric.isPercentage}
                tooltip={metric.tooltip}
                formattedValue={metric.formattedValue}
                metricsUpdatedAt={getContentMetricsUpdatedAt(content)}
              />
            ))}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

type MetricRow = {
  condition: boolean;
  icon: React.ReactNode;
  label: string;
  value: number;
  tooltip: string;
  formattedValue?: string;
  metricsUpdatedAt?: string | null;
};

const MetricsGrid: React.FC<{
  metrics: MetricRow[];
  layout: "horizontal" | "vertical";
}> = ({ metrics, layout }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns:
        layout === "horizontal"
          ? {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
            }
          : "1fr",
      gap: 1.5,
      alignItems: "stretch",
    }}
  >
    {metrics.map((metric, index) => (
      <MetricItem
        key={index}
        icon={metric.icon}
        label={metric.label}
        value={metric.value}
        tooltip={metric.tooltip}
        formattedValue={metric.formattedValue}
        vertical={layout === "vertical"}
        metricsUpdatedAt={metric.metricsUpdatedAt}
      />
    ))}
  </Box>
);

const MetricsSectionSubtitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 600,
      color: "text.secondary",
      textTransform: "uppercase",
      letterSpacing: 0.4,
      display: "block",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

// Component for rendering metrics in a card
const MetricsCard: React.FC<{
  content: PrivateContent;
  layout?: "horizontal" | "vertical";
  mentionTime?: number;
  includeEstimatedViewers?: boolean;
  includeInstagramReach?: boolean;
  includeLiveViewers?: boolean;
  liveAverageViewers?: number | null;
  livePeakViewers?: number | null;
  liveViewersAtMention?: number | null;
}> = ({
  content,
  layout = "horizontal",
  mentionTime,
  includeEstimatedViewers = false,
  includeInstagramReach = false,
  includeLiveViewers = false,
  liveAverageViewers = null,
  livePeakViewers = null,
  liveViewersAtMention = null,
}) => {
  const displayMetrics = getLatestContentMetrics(content, content.source);
  const metricsUpdatedAt = getContentMetricsUpdatedAt(content, content.source);
  const isMentionContext = mentionTime !== undefined;

  const estimatedViewers = isMentionContext
    ? getEstimatedViewersAtTime(content, mentionTime)
    : null;

  const onDemandMetrics: MetricRow[] = [
    {
      condition: displayMetrics.views > 0,
      icon: <VisibilityIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("vistas", "long"),
      value: displayMetrics.views,
      tooltip: getMetricTooltip("vistas", content.source),
    },
    {
      condition: displayMetrics.likes > 0,
      icon: <ThumbUpIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("likes", "long"),
      value: displayMetrics.likes,
      tooltip: getMetricTooltip("likes", content.source),
    },
    {
      condition: displayMetrics.comments > 0,
      icon: <ChatIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("comentarios", "long"),
      value: displayMetrics.comments,
      tooltip: getMetricTooltip("comentarios", content.source),
    },
    {
      condition: displayMetrics.shares > 0,
      icon: <ShareIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("shares", "long"),
      value: displayMetrics.shares,
      tooltip: getMetricTooltip("shares", content.source),
    },
    {
      condition:
        ((content.source === "youtube" && displayMetrics.views > 0) ||
          (((content.source as string) === "instagram" ||
            (content.source as string) === "instagram_private") &&
            (displayMetrics.reach > 0 || displayMetrics.views > 0))) &&
        getEngagementRate(content) > 0,
      icon: <TrendingUpIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("tasaEngagement", "long"),
      value: getEngagementRate(content),
      tooltip: getMetricTooltip("tasaEngagement"),
      formattedValue: `${getEngagementRate(content).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
    },
    {
      condition: content.source === "instagram" && displayMetrics.saves > 0,
      icon: <BookmarkIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("guardados", "long"),
      value: displayMetrics.saves,
      tooltip: getMetricTooltip("guardados", content.source),
    },
    {
      condition:
        includeInstagramReach &&
        content.source === "instagram" &&
        displayMetrics.reach > 0,
      icon: <PeopleIcon sx={{ fontSize: 16, color: "primary.main" }} />,
      label: getMetricLabel("alcance", "long"),
      value: displayMetrics.reach,
      tooltip: getMetricTooltip("alcance", content.source),
    },
  ]
    .filter((metric) => metric.condition)
    .map((metric) => ({ ...metric, metricsUpdatedAt }));

  const liveGeneralMetrics: MetricRow[] = [
    {
      condition:
        includeLiveViewers &&
        liveAverageViewers != null &&
        liveAverageViewers > 0,
      icon: (
        <VisibilityIcon
          sx={{ fontSize: 16, color: "primary.main", opacity: 0.8 }}
        />
      ),
      label: getMetricLabel("espectadoresEnVivoPromedio", "long"),
      value: liveAverageViewers || 0,
      tooltip: getMetricTooltip("espectadoresEnVivoPromedio"),
      formattedValue: formatViewerCount(liveAverageViewers || 0),
    },
    {
      condition:
        includeLiveViewers && livePeakViewers != null && livePeakViewers > 0,
      icon: (
        <VisibilityIcon
          sx={{ fontSize: 16, color: "primary.main", opacity: 0.8 }}
        />
      ),
      label: getMetricLabel("espectadoresEnVivoPeak", "long"),
      value: livePeakViewers || 0,
      tooltip: getMetricTooltip("espectadoresEnVivoPeak"),
      formattedValue: formatViewerCount(livePeakViewers || 0),
    },
  ].filter((metric) => metric.condition);

  const mentionMomentMetrics: MetricRow[] = [
    {
      condition:
        isMentionContext &&
        includeEstimatedViewers &&
        estimatedViewers !== null,
      icon: (
        <VisibilityIcon
          sx={{ fontSize: 16, color: "primary.main", opacity: 0.8 }}
        />
      ),
      label: getMetricLabel("espectadoresEstimados", "short"),
      value: estimatedViewers || 0,
      tooltip: `${getMetricTooltip("espectadoresEstimados")}. ${(estimatedViewers || 0).toLocaleString("es-ES")} de ${displayMetrics.views.toLocaleString("es-ES")} visualizaciones totales.`,
      formattedValue: formatViewerCount(estimatedViewers || 0),
    },
    {
      condition:
        isMentionContext &&
        includeLiveViewers &&
        liveViewersAtMention != null &&
        liveViewersAtMention > 0,
      icon: (
        <VisibilityIcon
          sx={{ fontSize: 16, color: "primary.main", opacity: 0.8 }}
        />
      ),
      label: getMetricLabel("espectadoresEnVivoMencion", "short"),
      value: liveViewersAtMention || 0,
      tooltip: getMetricTooltip("espectadoresEnVivoMencion"),
      formattedValue: formatViewerCount(liveViewersAtMention || 0),
    },
  ].filter((metric) => metric.condition);

  const hasAnyMetrics =
    onDemandMetrics.length > 0 ||
    liveGeneralMetrics.length > 0 ||
    mentionMomentMetrics.length > 0;

  if (!hasAnyMetrics) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2">Sin métricas disponibles</Typography>
        </CardContent>
      </Card>
    );
  }

  const audienceRows = buildYoutubeAudienceMetricRows(content);
  if (
    onDemandMetrics.length === 0 &&
    liveGeneralMetrics.length === 0 &&
    mentionMomentMetrics.length === 0 &&
    audienceRows.length > 0
  ) {
    return null;
  }

  const showGenerales =
    onDemandMetrics.length > 0 || liveGeneralMetrics.length > 0;
  const showOnDemandSubtitle =
    onDemandMetrics.length > 0 && liveGeneralMetrics.length > 0;
  const showLiveSubtitle =
    liveGeneralMetrics.length > 0 && onDemandMetrics.length > 0;
  const showMentionSection = mentionMomentMetrics.length > 0;

  return (
    <Card>
      <CardContent sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {showGenerales && (
            <Box>
              {(showMentionSection ||
                (onDemandMetrics.length > 0 &&
                  liveGeneralMetrics.length > 0)) && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1.5 }}
                >
                  Generales
                </Typography>
              )}
              <Stack spacing={2}>
                {onDemandMetrics.length > 0 && (
                  <Box>
                    {showOnDemandSubtitle && (
                      <MetricsSectionSubtitle>On-demand</MetricsSectionSubtitle>
                    )}
                    <MetricsGrid metrics={onDemandMetrics} layout={layout} />
                  </Box>
                )}
                {liveGeneralMetrics.length > 0 && (
                  <Box>
                    {showLiveSubtitle && (
                      <MetricsSectionSubtitle>En vivo</MetricsSectionSubtitle>
                    )}
                    <MetricsGrid metrics={liveGeneralMetrics} layout={layout} />
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {showMentionSection && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                En el momento de la mención
              </Typography>
              <MetricsGrid metrics={mentionMomentMetrics} layout={layout} />
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export const ContentStatistics: React.FC<ContentStatisticsProps> = ({
  content,
  linkedContent,
  showTitle = true,
  title = "Métricas",
  mentionTime,
  includeEstimatedViewers = false,
  includeInstagramReach = false,
  includeLiveViewers = false,
  liveViewers = null,
  liveAverageViewers = null,
  livePeakViewers = null,
  liveViewersAtMention = null,
  onContentUpdated,
  readOnly = false,
}) => {
  const [currentContent, setCurrentContent] = useState<PrivateContent>(content);
  const [linkedContentOverride, setLinkedContentOverride] =
    useState<PrivateContent | null>(null);

  useEffect(() => {
    setLinkedContentOverride(null);
  }, [linkedContent?._id]);

  const contentId = String(content._id);
  useEffect(() => {
    setCurrentContent(content);
  }, [content, contentId]);

  const displayLinkedContent = linkedContentOverride ?? linkedContent ?? null;
  const linkedRowForUi =
    linkedContent != null ? (displayLinkedContent ?? linkedContent) : null;

  const resolvedLiveAverage =
    liveAverageViewers ??
    (mentionTime === undefined ? liveViewers : null);
  const resolvedLiveAtMention =
    liveViewersAtMention ??
    (mentionTime !== undefined ? liveViewers : null);

  const handleContentUpdated = (updatedContent: PrivateContent) => {
    if (String(updatedContent._id) === String(currentContent._id)) {
      setCurrentContent(updatedContent);
    }
    if (linkedContent && String(updatedContent._id) === String(linkedContent._id)) {
      setLinkedContentOverride(updatedContent);
    }
    if (onContentUpdated) {
      onContentUpdated(updatedContent);
    }
  };

  const handleMetricsUpdated = (metrics: {
    streams: number;
    uniqueListeners: number;
  }) => {
    const updatedContent = {
      ...currentContent,
      insights: {
        ...currentContent.insights,
        ...metrics,
      },
    };
    handleContentUpdated(updatedContent);
  };

  const hasLinkedContent = linkedContent != null;
  const isStandaloneSpotify = currentContent.source === "spotify";
  const hasLiveMetricsToShow =
    includeLiveViewers &&
    ((resolvedLiveAverage != null && resolvedLiveAverage > 0) ||
      (livePeakViewers != null && livePeakViewers > 0) ||
      (resolvedLiveAtMention != null && resolvedLiveAtMention > 0));
  const hasEstimatedAtMention =
    includeEstimatedViewers &&
    mentionTime !== undefined &&
    getEstimatedViewersAtTime(currentContent, mentionTime) != null;

  if (
    !hasDisplayableMetrics(currentContent) &&
    !hasLinkedContent &&
    !isStandaloneSpotify &&
    !hasLiveMetricsToShow &&
    !hasEstimatedAtMention
  ) {
    return null;
  }

  const primaryDialogContentId =
    normalizePrivateContentId(currentContent) ?? undefined;

  const renderPlatformMetrics = (
    row: PrivateContent,
    opts: { onSpotifyUpdated?: typeof handleMetricsUpdated; showSource?: boolean },
  ) => {
    if (row.source === "spotify") {
      return (
        <>
          {opts.showSource ? (
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <SourcePlatform contentSource={row.source} iconSize={20} />
            </Box>
          ) : null}
          <SpotifyMetricsCard
            key={`spotify-metrics-${String(row._id)}`}
            contentId={String(row._id)}
            onMetricsUpdated={opts.onSpotifyUpdated}
            readOnly={readOnly}
          />
        </>
      );
    }

    const isYoutube = row.source === "youtube";

    return (
      <>
        <MetricsCard
          content={row}
          layout="horizontal"
          mentionTime={mentionTime}
          includeEstimatedViewers={includeEstimatedViewers}
          includeInstagramReach={includeInstagramReach}
          includeLiveViewers={includeLiveViewers}
          liveAverageViewers={isYoutube ? resolvedLiveAverage : null}
          livePeakViewers={isYoutube ? livePeakViewers : null}
          liveViewersAtMention={isYoutube ? resolvedLiveAtMention : null}
        />
        <YoutubeAudienceSection
          content={row}
          onContentUpdated={handleContentUpdated}
          readOnly={readOnly}
          primaryDialogContentId={primaryDialogContentId}
        />
      </>
    );
  };

  return (
    <Box>
      {showTitle && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h3" color="text">
              {title}
            </Typography>
            <Tooltip
              title={getMultiAccountShareTooltip()}
              arrow
              placement="top"
            >
              <IconButton size="small" sx={{ color: "text.secondary" }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      )}

      {/*
        Single tree for primary (+ optional linked) metrics so SpotifyMetricsCard
        does not remount when linked content arrives mid-edit (which wiped typed values).
      */}
      <Stack spacing={2}>
        {renderPlatformMetrics(currentContent, {
          onSpotifyUpdated: handleMetricsUpdated,
        })}
        {linkedRowForUi
          ? renderPlatformMetrics(linkedRowForUi, { showSource: true })
          : null}
      </Stack>
    </Box>
  );
};
